import React, { useState, useEffect, useRef } from 'react';
import { FaSearch, FaPaw, FaHandPaper, FaTrophy } from 'react-icons/fa';
import { HiSparkles } from 'react-icons/hi';
import ModuleHeader from "../../moduleHeader";

const defaultPackageData = {
  user: {
    first_name: "Kullanıcı",
    last_name: ""
  },
  package: {
    name: "One Minute Success",
    description: "Bir dakikada başarı hissi yaratın. Basit görevlerle kendinizi başarılı hissettirin ve motivasyonunuzu artırın.",
    image_url: "https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=800&q=80"
  }
};

const challenges = [
  {
    id: 1,
    title: "Sarı Bir Nesne Bul",
    description: "Etrafınızda sarı bir nesne bulun ve ona odaklanın.",
    icon: FaSearch,
    timeLimit: 60,
    placeholder: "Bulduğunuz sarı nesneyi yazın..."
  },
  {
    id: 2,
    title: "Bir Hayvan Sesi Çıkar",
    description: "Bir hayvan sesi çıkarın ve eğlenin!",
    icon: FaPaw,
    timeLimit: 10,
    placeholder: "Hangi hayvan sesi çıkardınız?"
  },
  {
    id: 3,
    title: "Elindeki Eşyayı Tarif Et",
    description: "Elindeki eşyayı 10 saniye detaylı tarif et.",
    icon: FaHandPaper,
    timeLimit: 10,
    placeholder: "Eşyanızı detaylıca tarif edin..."
  }
];

export default function OneMinuteSuccess({ id, moduleId, isOwner }) {
  const [currentChallengeIndex, setCurrentChallengeIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState(challenges[0].timeLimit);
  const [isActive, setIsActive] = useState(false);
  const [answers, setAnswers] = useState({});
  const [completedChallenges, setCompletedChallenges] = useState([]);
  const [packageData] = useState(defaultPackageData);
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(false);
  const intervalRef = useRef(null);

  const currentChallenge = challenges[currentChallengeIndex];

  useEffect(() => {
    if (isActive && timeLeft > 0) {
      intervalRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            setIsActive(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isActive, timeLeft]);

  const handleStart = () => {
    setIsActive(true);
    setTimeLeft(currentChallenge.timeLimit);
  };

  const handleStop = () => {
    setIsActive(false);
  };

  const handleAnswerChange = (value) => {
    setAnswers(prev => ({
      ...prev,
      [currentChallenge.id]: value
    }));
  };

  const handleComplete = () => {
    if (!completedChallenges.includes(currentChallenge.id)) {
      setCompletedChallenges([...completedChallenges, currentChallenge.id]);
    }
    setIsActive(false);
  };

  const handleNext = () => {
    if (currentChallengeIndex < challenges.length - 1) {
      setCurrentChallengeIndex(currentChallengeIndex + 1);
      setTimeLeft(challenges[currentChallengeIndex + 1].timeLimit);
      setIsActive(false);
    }
  };

  const handleBack = () => {
    if (currentChallengeIndex > 0) {
      setCurrentChallengeIndex(currentChallengeIndex - 1);
      setTimeLeft(challenges[currentChallengeIndex - 1].timeLimit);
      setIsActive(false);
    }
  };

  const handleRestart = () => {
    setCurrentChallengeIndex(0);
    setTimeLeft(challenges[0].timeLimit);
    setIsActive(false);
    setAnswers({});
    setCompletedChallenges([]);
  };

  const allCompleted = completedChallenges.length === challenges.length;

  return (
    <div className="w-full h-screen min-h-screen flex items-stretch justify-stretch sm:items-center sm:justify-center bg-[#e8e9ed] relative">
      <div className="flex flex-col items-center w-full min-h-full bg-white shadow-xl border border-gray-200 rounded-none sm:max-w-[390px] sm:h-[700px] sm:rounded-3xl relative overflow-y-auto">
        {/* ModuleHeader */}
        <div className="relative z-30 w-full mb-2">
          <ModuleHeader
            userName={packageData.user?.first_name + " " + packageData.user?.last_name}
            subtitle={packageData.package?.name + " Modülüne Hoş Geldiniz"}
            title={packageData.package?.name}
            description={packageData.package?.description}
            backgroundImage={packageData.package?.image_url}
            onExpandedChange={setIsHeaderExpanded}
            showToggle={true}
            autoHeight={true}
          />
        </div>

        <div className="w-full px-6 py-8 pb-24 flex-1">
          {!allCompleted ? (
            <>
              {/* Progress */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-gray-400 font-medium">
                    {currentChallengeIndex + 1} / {challenges.length}
                  </span>
                  <span className="text-xs text-gray-400 font-medium">
                    {Math.round(((currentChallengeIndex + 1) / challenges.length) * 100)}%
                  </span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-1">
                  <div 
                    className="bg-[#3DA7B5] h-1 rounded-full transition-all duration-500"
                    style={{ width: `${((currentChallengeIndex + 1) / challenges.length) * 100}%` }}
                  ></div>
                </div>
              </div>

              {/* Challenge Card */}
              <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-6">
                <div className="text-center mb-6">
                  <div className="flex justify-center mb-5">
                    {(() => {
                      const IconComponent = currentChallenge.icon;
                      return <IconComponent className="text-5xl text-[#3DA7B5]" />;
                    })()}
                  </div>
                  <h2 className="text-xl font-semibold text-gray-800 mb-2">{currentChallenge.title}</h2>
                  <p className="text-gray-500 text-sm mb-6">{currentChallenge.description}</p>
                  
                  {/* Timer */}
                  <div className="mb-6">
                    <div className={`text-4xl font-light mb-3 ${
                      timeLeft <= 10 ? 'text-red-500' : 'text-[#3DA7B5]'
                    }`}>
                      {timeLeft}s
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-1">
                      <div 
                        className={`h-1 rounded-full transition-all duration-1000 ${
                          timeLeft <= 10 ? 'bg-red-500' : 'bg-[#3DA7B5]'
                        }`}
                        style={{ width: `${(timeLeft / currentChallenge.timeLimit) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                </div>

                <div>
                  <textarea
                    value={answers[currentChallenge.id] || ''}
                    onChange={(e) => handleAnswerChange(e.target.value)}
                    placeholder={currentChallenge.placeholder}
                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#3DA7B5] focus:border-[#3DA7B5] resize-none text-sm"
                    rows="4"
                  />
                </div>
              </div>

              {/* Control Buttons */}
              <div className="space-y-2 mb-4">
                {!isActive && timeLeft > 0 && (
                  <button
                    onClick={handleStart}
                    className="w-full bg-[#3DA7B5] hover:bg-[#2F8894] text-white rounded-full py-4 font-medium text-base transition-all duration-300"
                  >
                    Başlat
                  </button>
                )}
                {isActive && (
                  <button
                    onClick={handleStop}
                    className="w-full bg-gray-400 hover:bg-gray-500 text-white rounded-full py-4 font-medium text-base transition-all duration-300"
                  >
                    Durdur
                  </button>
                )}
                {!isActive && answers[currentChallenge.id]?.trim() && (
                  <button
                    onClick={handleComplete}
                    className="w-full bg-[#3DA7B5] hover:bg-[#2F8894] text-white rounded-full py-3 font-medium text-sm transition-all duration-300"
                  >
                    Tamamla
                  </button>
                )}
              </div>

              {/* Navigation */}
              <div className="flex gap-2">
                {currentChallengeIndex > 0 && (
                  <button
                    onClick={handleBack}
                    className="flex-1 bg-white border border-gray-200 hover:border-gray-300 text-gray-600 hover:text-gray-700 rounded-full py-3 font-medium text-sm transition-all duration-300"
                  >
                    Geri
                  </button>
                )}
                {currentChallengeIndex < challenges.length - 1 && (
                  <button
                    onClick={handleNext}
                    className="flex-1 bg-white border border-gray-200 hover:border-gray-300 text-gray-600 hover:text-gray-700 rounded-full py-3 font-medium text-sm transition-all duration-300"
                  >
                    İleri
                  </button>
                )}
              </div>

              {/* Completed Challenges */}
              {completedChallenges.length > 0 && (
                <div className="mt-8 pt-6 border-t border-gray-100">
                  <p className="text-xs text-gray-400 mb-3 font-medium">Tamamlanan Görevler</p>
                  <div className="flex flex-wrap gap-2">
                    {completedChallenges.map(challengeId => {
                      const challenge = challenges.find(c => c.id === challengeId);
                      const IconComponent = challenge.icon;
                      return (
                        <span key={challengeId} className="bg-[#3DA7B5]/10 text-[#3DA7B5] px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5">
                          <IconComponent className="text-xs" /> {challenge.title}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center animate-fadeIn">
              <div className="flex justify-center mb-8">
                <FaTrophy className="text-6xl text-[#3DA7B5]" />
              </div>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">Tüm Görevler Tamamlandı</h2>
              <p className="text-gray-500 text-sm mb-8 leading-relaxed">
                Bir dakikada başarı hissi yarattınız. Bu başarılarınızı hatırlayın ve motivasyonunuzu koruyun.
              </p>
              <button
                onClick={handleRestart}
                className="w-full bg-[#3DA7B5] hover:bg-[#2F8894] text-white rounded-full py-4 font-medium text-base transition-all duration-300"
              >
                Tekrar Başla
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

